/**
 * List of places in the map sidebar
 */

import { Card } from '@/components/ui/card';
import { MapPin } from 'lucide-react';
import { usePlacesData } from '@/data/places/places-data-context';
import { useMemo } from 'react';
import { getStandardPeriod, PERIOD_DEFINITIONS } from '@/lib/utils/historical-periods-shared';
import { findPlaceForEntity } from '@/data/places/place-images-fetch';

interface GeographicEntityWithRelevance extends GeographicEntity {
    relevanceType?: string;
    relevanceReason?: string;
}

interface PlacesListProps {
    places: GeographicEntityWithRelevance[];
    onPlaceClick: (placeName: string, coordinates: { latitude: number; longitude: number }) => void;
    passageYear?: number | null;
}

export function PlacesList({ places, onPlaceClick, passageYear }: PlacesListProps) {
    const { getPlaceByName } = usePlacesData();

    // Get Place data for each entity to access images
    const placesWithData = useMemo(() => {
        return places.map(entity => ({
            entity,
            placeData: findPlaceForEntity(entity, getPlaceByName)
        }));
    }, [places, getPlaceByName]);

    // Memoize the places with their selected reconstruction URLs
    // This prevents recalculating for all places on every render
    const placesWithImages = useMemo(() => {
        // Helper function to select the best reconstruction based on passage year
        const selectReconstructionUrl = (reconstructionsByPeriod: Place['images']['reconstructionsByPeriod']): string | undefined => {
            if (!reconstructionsByPeriod) return undefined;

            const availablePeriods = Object.keys(reconstructionsByPeriod) as HistoricalPeriod[];
            if (availablePeriods.length === 0) return undefined;

            // If we have a passage year, find the matching period
            if (passageYear !== null && passageYear !== undefined) {
                const matchingPeriod = getStandardPeriod(passageYear);
                if (matchingPeriod && availablePeriods.includes(matchingPeriod)) {
                    return reconstructionsByPeriod[matchingPeriod]?.imageUrl;
                }
            }

            // Fallback: Most recent period (likely most detailed)
            const sortedPeriods = availablePeriods.sort((a, b) => {
                const aEnd = PERIOD_DEFINITIONS[a]?.end || 0;
                const bEnd = PERIOD_DEFINITIONS[b]?.end || 0;
                return bEnd - aEnd; // Descending order (most recent first)
            });

            return reconstructionsByPeriod[sortedPeriods[0]]?.imageUrl;
        };

        return placesWithData.map(({ entity, placeData }) => {
            const selectedReconstructionUrl = placeData?.images?.reconstructionsByPeriod
                ? selectReconstructionUrl(placeData.images.reconstructionsByPeriod)
                : undefined;
            // Prioritize reconstruction image over ruins image
            const imageUrl = selectedReconstructionUrl || placeData?.images?.ruinsImageUrl;
            const hasImage = imageUrl && placeData?.images?.generationStatus === 'completed';

            return {
                entity,
                placeData,
                imageUrl,
                hasImage
            };
        });
    }, [placesWithData, passageYear]);

    return (
        <div>
            <h3 className="text-sm font-semibold mb-3">
                Places ({places.length})
            </h3>
            <div className="space-y-2">
                {placesWithImages.map(({ entity, imageUrl, hasImage }, index) => (
                    <Card
                        key={index}
                        className="p-3 cursor-pointer hover:bg-accent transition-colors"
                        onClick={() => onPlaceClick(entity.placeName, entity.coordinates)}
                    >
                        <div className="flex gap-3">
                            {/* Place image */}
                            {hasImage ? (
                                <div className="flex-shrink-0 w-16 h-16 rounded overflow-hidden bg-muted">
                                    <img
                                        src={imageUrl}
                                        alt={entity.placeName}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            ) : (
                                <div className="flex-shrink-0 w-16 h-16 rounded bg-muted flex items-center justify-center">
                                    <MapPin className="h-6 w-6 text-muted-foreground" />
                                </div>
                            )}

                            {/* Place info */}
                            <div className="flex-1 min-w-0 space-y-1">
                                <div className="flex items-start gap-2">
                                    <span className="font-medium text-sm break-words">{entity.placeName}</span>
                                </div>
                                <p className="text-xs text-muted-foreground line-clamp-3 break-words">
                                    {entity.summary}
                                </p>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
}

